import { useEffect, useState, useMemo, useContext, useRef } from "react";
import styles from "./store.module.css";
import { GoBackButton } from "../../../components/buttons/goBack";
import { CardOne } from "../../../components/cards/cardOne";
import {
  focusOnMouseMove,
  navigationUserInputHandler,
  navigationGamepadLoop,
  playSound,
} from "../../../utilities/utilities";
import { themeContext } from "../../../context/theme";
import { useAuth } from "../../../context/auth";
import { useBoardSkin, PREMIUM_BOARDS } from "../../../context/boardSkin";
import {
  isBoardInTrial,
  getTrialTimeRemaining,
  STATIC_APP_SKIN_ITEMS,
  STATIC_BOARD_SKIN_ITEMS,
} from "../../../utilities/trialConfig";
import creatorImage from "../../../assets/images/creator.jpg";
import defaultImage from "../../../assets/images/default.png";
import { ThemeLoader } from "../../../components/svg/themeLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import BoardPreviewModal from "../../../components/modals/BoardPreviewModal";
import SkinPreviewModal, {
  THEME_PREVIEWS,
} from "../../../components/modals/SkinPreviewModal";
import PremiumBoardPreviewModal from "../../../components/modals/PremiumBoardPreviewModal";

const blazeImage = "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1772477732/blaze/blaze_rlqcsn.png";
const spectrumImage = "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1772477772/spectrum/spectrum_kwggsw.png";

const API_BASE_URL =
  process.env.REACT_APP_SERVER_URL || "http://localhost:8080";

export const Store = ({ setShowStore, game }) => {
  const storeCloseButtonClickHandler = () => {
    setShowStore(false);
  };

  const {
    setTheme,
    theme,
    themeName,
    allThemes,
    syncOwnedThemesFromItems,
    trialActive,
  } = useContext(themeContext);
  const { user, signIn } = useAuth();
  const {
    selectedPremiumBoard,
    setPremiumBoard,
    ownedBoardIds,
    trialActive: boardTrialActive,
    syncOwnedBoardsFromItems,
  } = useBoardSkin();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allItemsData, setAllItemsData] = useState([]);
  const [itemsApiError, setItemsApiError] = useState(false);
  const [previewBoard, setPreviewBoard] = useState(null);
  const [showSkinPreview, setShowSkinPreview] = useState(false);
  const [previewOpenedFromPayment, setPreviewOpenedFromPayment] =
    useState(false);
  const [trialTimeRemaining, setTrialTimeRemaining] = useState(0);

  const [startGamePadLoop, setStartGamePadLoop] = useState(true);
  const gamepadLoopState = useMemo(() => {
    return { run: true, loopStarted: false };
  }, []);
  // Ref to track whether store gamepad/keyboard navigation should be active
  const storeNavActiveRef = useRef(true);
  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Hardcoded item ID to CSS theme mapping
  // Item IDs from database: 1=Crimson, 2=Emerald, 3=Violet, 4=Golden, 5=Cyan
  // Maps to CSS classes: theme-0, theme-1, theme-2, theme-3, theme-4
  const itemNameToThemeClass = {
    "Blaze": "theme-0",
    "Frost": "theme-4",
    "Spectrum": "theme-5",
  };

  const itemNameToCssColor = {
    "Blaze": "red",
    "Frost": "blue",
    "Spectrum": "spectrum",
  };

  const itemNameToGumroadUrl = {
    "Frost": "https://errorrunner.gumroad.com/l/bahat?wanted=true",
    "Spectrum": "https://errorrunner.gumroad.com/l/emlchk?wanted=true",
  };

  const itemNameToProductUrl = {
    "Frost": "https://errorrunner.gumroad.com/l/bahat",
    "Spectrum": "https://errorrunner.gumroad.com/l/emlchk",
  };

  // Check if theme has preview images available
  const hasPreviewImages = (themeName) => {
    return themeName === "Frost" || themeName === "Spectrum" || themeName === "Blaze";
  };

  // Fetch items with ownership when store opens
  useEffect(() => {
    const fetchItems = async () => {
      setIsLoading(true);
      // Reset stale data from any previous fetch before loading fresh data
      setAllItemsData([]);
      setItemsApiError(false);
      syncOwnedBoardsFromItems([]);

      if (!user) {
        // Not logged in — build from hardcoded static config
        const staticItems = [
          ...STATIC_APP_SKIN_ITEMS.map((item) => ({
            ...item,
            color: itemNameToCssColor[item.name] || "red",
            cssClass: itemNameToThemeClass[item.name] || "theme-0",
            gumroadUrl: itemNameToGumroadUrl[item.name] || null,
            productUrl: itemNameToProductUrl[item.name] || null,
          })),
          ...STATIC_BOARD_SKIN_ITEMS,
        ];
        setAllItemsData(staticItems);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/user/items`, {
          credentials: "include",
        });

        if (!response.ok) throw new Error("Failed to fetch items");

        const data = await response.json();

        if (data.success) {
          // Sync owned themes and boards into their contexts
          syncOwnedThemesFromItems(data.items);
          syncOwnedBoardsFromItems(data.items);

          // Add UI properties to THEME items; BOARD_SKIN items kept as-is
          const itemsWithExtras = data.items.map((item) =>
            item.type === "THEME"
              ? {
                  ...item,
                  color: itemNameToCssColor[item.name] || "red",
                  cssClass: itemNameToThemeClass[item.name] || "theme-0",
                  gumroadUrl: itemNameToGumroadUrl[item.name] || null,
                  productUrl: itemNameToProductUrl[item.name] || null,
                }
              : item,
          );
          setAllItemsData(itemsWithExtras);
        }
      } catch (error) {
        // API failed — clear data and show error in UI
        setAllItemsData([]);
        setItemsApiError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [user]); // Refetch when user changes (login/logout)

  // Trial timer - update every second
  useEffect(() => {
    if (!user || !user.trialStart) {
      setTrialTimeRemaining(0);
      return;
    }

    // Initial calculation
    const updateTimer = () => {
      const remaining = getTrialTimeRemaining(user.trialStart);
      setTrialTimeRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Format time remaining as HH:MM:SS
  const formatTimeRemaining = (ms) => {
    if (ms <= 0) return "Trial finished";

    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `Trial: ${hours}h ${minutes}m ${seconds}s`;
  };

  const themes = allItemsData.filter((item) => item.type === "THEME");
  const boardItemsData = allItemsData.filter(
    (item) => item.type === "BOARD_SKIN",
  );

  // Check if user owns a theme (or has trial access)
  const userOwnsTheme = (themeData) => {
    if (themeData.isTrialAccessible) return true;
    if (themeData.hasOwnProperty("isOwned")) {
      return themeData.isOwned;
    }
    if (!themeData.isPaid) return true;
    return false;
  };

  // Check if user owns a premium board (based on DB board skin ownership + trial)
  const userOwnsBoard = (board) => {
    if (board.isFree) return true;

    // If we have API data, use it (most accurate)
    if (boardItemsData && board.boardItemName) {
      const apiBoard = boardItemsData.find(
        (item) => item.name === board.boardItemName,
      );
      if (apiBoard) {
        return apiBoard.isOwned || apiBoard.isTrialAccessible;
      }
    }

    // Fallback to context-based checks (for non-authenticated users)
    if (ownedBoardIds && ownedBoardIds.includes(board.id)) return true;
    if (boardTrialActive && isBoardInTrial(board.id)) return true;
    return false;
  };

  const handleThemeAction = async (themeData) => {
    if (!themeData.isPaid || userOwnsTheme(themeData)) {
      // Free or owned theme - apply directly
      game.sounds.clickSound.play();
      try {
        await setTheme(themeData.name);
        game.userSavedData.settings.skin = themeData.cssClass;
        game.saveData();
      } catch (error) {
        // Toast already shown by changeTheme
      }
    } else {
      // Paid theme - show payment modal (login not required, handled by Gumroad)
      game.sounds.clickSound.play();
      setSelectedTheme(themeData);
      setShowPaymentModal(true);
      setPaymentError(null);
    }
  };

  const handleViewProduct = () => {
    if (!selectedTheme) return;

    // Show skin preview modal (from payment modal)
    game.sounds.clickSound.play();
    setShowPaymentModal(false);
    setPreviewOpenedFromPayment(true);
    setShowSkinPreview(true);
  };

  const handlePreviewSkin = (themeData, e) => {
    if (e) {
      e.stopPropagation();
    }
    game.sounds.clickSound.play();
    setSelectedTheme(themeData);
    setPreviewOpenedFromPayment(false);
    setShowSkinPreview(true);
  };

  const handlePaymentProcess = async () => {
    if (!selectedTheme) return;

    // Open Gumroad purchase link (checkout URL)
    if (selectedTheme.gumroadUrl) {
      game.sounds.clickSound.play();

      // Add email query parameter if user is logged in
      let purchaseUrl = selectedTheme.gumroadUrl;
      if (user && user.email) {
        const urlObj = new URL(selectedTheme.gumroadUrl);
        urlObj.searchParams.set("email", user.email);
        purchaseUrl = urlObj.toString();
      }

      window.open(purchaseUrl, "_blank");
      setShowPaymentModal(false);
    } else {
      setPaymentError("Purchase link not available.");
    }
  };

  const handleLoginFromModal = async () => {
    try {
      setShowLoginModal(false);
      await signIn(window.location.origin + "/");
    } catch (error) {
      // Sign in error handled silently
    }
  };

  const optionsClickHandler = (event) => {
    event.stopPropagation();
    // Find the theme card element (could be the clicked element or a parent)
    let element = event.target;
    let themeName = element.getAttribute("data-theme");

    // If clicked element doesn't have data-theme, check parent elements
    while (!themeName && element.parentElement) {
      element = element.parentElement;
      themeName = element.getAttribute("data-theme");
    }

    if (themeName) {
      const themeData = themes.find((t) => t.name === themeName);
      if (themeData) {
        // Open preview modal instead of directly selecting/purchasing
        game.sounds.clickSound.play();
        setSelectedTheme(themeData);
        setPreviewOpenedFromPayment(false);
        setShowSkinPreview(true);
      }
    }
  };

  // Keep storeNavActiveRef in sync with modal state (avoids stale closure in gamepad loop)
  useEffect(() => {
    storeNavActiveRef.current =
      !showPaymentModal && !showLoginModal && !showSkinPreview && !previewBoard;
  }, [showPaymentModal, showLoginModal, showSkinPreview, previewBoard]);

  useEffect(() => {
    if (
      gamepadLoopState.run &&
      !isLoading &&
      !showPaymentModal &&
      !showLoginModal &&
      !showSkinPreview &&
      !previewBoard
    ) {
      if (!gamepadLoopState.loopStarted) {
        gamepadLoopState.loopStarted = true;
        navigationGamepadLoop(
          game,
          (event) => {
            if (storeNavActiveRef.current) {
              navigationUserInputHandler(
                event,
                focusableElements,
                game,
                () => {
                  gamepadLoopState.run = false;
                  setShowStore(false);
                },
                playSound,
              );
            }
          },
          gamepadLoopState,
        );
      }
    }
  }, [
    startGamePadLoop,
    isLoading,
    showPaymentModal,
    showLoginModal,
    showSkinPreview,
    previewBoard,
  ]);

  useEffect(() => {
    if (!isLoading) {
      focusableElements.elements = document.querySelectorAll("[tabindex='3']");
    }
  }, [isLoading]);

  useEffect(() => {
    if (!isLoading) {
      focusableElements.elements[focusableElements.index]?.focus();
    }
  }, [gamepadLoopState.run, isLoading]);

  useEffect(() => {
    return () => {
      gamepadLoopState.run = false;
    };
  }, []);

  // Re-focus store element when any preview modal closes
  useEffect(() => {
    if (
      !isLoading &&
      !showSkinPreview &&
      !previewBoard &&
      !showPaymentModal &&
      !showLoginModal
    ) {
      focusableElements.elements = document.querySelectorAll("[tabindex='3']");
      focusableElements.elements[focusableElements.index]?.focus();
    }
  }, [showSkinPreview, previewBoard, showPaymentModal, showLoginModal]);

  // Auto-focus payment modal when it opens
  useEffect(() => {
    if (showPaymentModal) {
      const timer = setTimeout(() => {
        const modal = document.querySelector('[data-payment-modal="true"]');
        if (modal) {
          modal.focus();
          focusableElements.paymentModalIndex = 0;
          const buttons = modal.querySelectorAll('[data-modal-button="true"]');
          if (buttons[0]) {
            buttons[0].focus();
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Refocus store when modal closes
      if (
        focusableElements.elements &&
        focusableElements.elements[focusableElements.index]
      ) {
        focusableElements.elements[focusableElements.index].focus();
      }
    }
  }, [showPaymentModal]);

  const getThemeStatus = (themeData) => {
    if (themeName === themeData.name) return "CURRENT";
    if (!themeData.isPaid) return "FREE";
    if (themeData.isOwned) return "OWNED";
    if (themeData.isTrialAccessible) return "TRIAL";
    return "BUY";
  };

  // Get thumbnail image for premium board
  const getBoardThumbnail = (board) => {
    if (!board.thumbnail) return null;
    const imageMap = {
      "default.png": defaultImage,
      "inferno.png": blazeImage,
      "frost.png":
        "https://res.cloudinary.com/dfykcrezn/image/upload/q_auto,f_auto/v1771014083/frost/frost_t6tlnt.png",
      "spectrum.png": spectrumImage,
    };
    return imageMap[board.thumbnail] || null;
  };

  // Get thumbnail image for app skin — use first preview image if available
  const getThemeThumbnail = (themeName) => {
    return THEME_PREVIEWS[themeName]?.images?.[0] || null;
  };

  return (
    <>
      <CardOne
        customTag="section"
        attributes={{
          className: styles["store-section"],
          onKeyDown: (event) => {
            if (
              !isLoading &&
              !showPaymentModal &&
              !showLoginModal &&
              !showSkinPreview &&
              !previewBoard
            ) {
              navigationUserInputHandler(
                event,
                focusableElements,
                game,
                () => {
                  gamepadLoopState.run = false;
                  setShowStore(false);
                },
                playSound,
              );
            }
          },
          onMouseMove: (event) => {
            if (
              gamepadLoopState.run &&
              !isLoading &&
              !showPaymentModal &&
              !showLoginModal &&
              !showSkinPreview &&
              !previewBoard
            ) {
              focusOnMouseMove(event, focusableElements, game, playSound);
            }
          },
          tabIndex: -1,
        }}
      >
        {isLoading ? (
          <div className={styles["loader-container"]}>
            <ThemeLoader theme={theme} />
            <div className={styles["loading-text"]}>Loading store...</div>
          </div>
        ) : (
          <>
            <GoBackButton
              onClickHandler={storeCloseButtonClickHandler}
              game={game}
            ></GoBackButton>
            <div className={styles["store-content"]}>
              <div className={styles["store-header"]}>
                <h1 className={styles["store-heading"]}>Store</h1>
                {user && (trialActive || boardTrialActive) && (
                  <div className={styles["trial-timer"]}>
                    {formatTimeRemaining(trialTimeRemaining)}
                  </div>
                )}
              </div>

              {user && itemsApiError ? (
                <p className={styles["items-error"]}>Something went wrong.</p>
              ) : (
                <>
                  {/* App Skins Section */}
                  <div className={styles["skins-section"]}>
                    <h2 className={styles["skins-heading"]}>App Skins</h2>
                    <p className={styles["skins-description"]}>
                      Transform the entire application with beautiful skins.
                      Each skin applies a unique color scheme across all
                      screens.
                    </p>
                    <div
                      className={styles["themes-container"]}
                      onClick={optionsClickHandler}
                    >
                      {themes
                        .filter((themeData) => {
                          // Hide Nova, Pulse, and Aura for now (filter by name, not ID — IDs differ across environments)
                          return !["Pulse", "Aura", "Nova"].includes(themeData.name);
                        })
                        .map((themeData) => {
                          const themeThumbnail = getThemeThumbnail(
                            themeData.name,
                          );
                          return (
                            <div
                              key={themeData.id}
                              className={styles["theme-wrapper"]}
                            >
                              <div
                                tabIndex={3}
                                className={
                                  styles["option"] +
                                  " " +
                                  styles[themeData.color] +
                                  (themeName === themeData.name
                                    ? " " + styles["current"]
                                    : "") +
                                  (userOwnsTheme(themeData)
                                    ? " " + styles["owned"]
                                    : "") +
                                  (themeThumbnail
                                    ? " " + styles["has-theme-thumbnail"]
                                    : "")
                                }
                                style={
                                  themeThumbnail
                                    ? {
                                        backgroundImage: `url(${themeThumbnail})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center center",
                                        backgroundRepeat: "no-repeat",
                                      }
                                    : {}
                                }
                                data-theme={themeData.name}
                              ></div>
                              <div className={styles["theme-name"]}>
                                {themeData.name}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Board Skins Section */}
                  <div className={styles["premium-section"]}>
                    <h2 className={styles["premium-heading"]}>Board Skins</h2>
                    <p className={styles["premium-description"]}>
                      Elevate your gameplay with immersive board experiences.
                      Each board skin features:
                    </p>
                    <ul className={styles["board-features-list"]}>
                      <li>
                        Unique animation and sound effects for single line clear
                      </li>
                      <li>
                        Unique animation and sound effects for multi line clear
                      </li>
                      <li>
                        Unique sound effects and animations for piece movements
                      </li>
                      <li>
                        Unique sound effects and animations for hard drops
                      </li>
                      <li>
                        Overall unique look and feel
                      </li>
                    </ul>
                    <div className={styles["premium-boards-container"]}>
                      {allItemsData.length > 0 &&
                        PREMIUM_BOARDS.filter((board) => board.id !== "none").map((board) => {
                          const thumbnail = getBoardThumbnail(board);
                          const isOwned = userOwnsBoard(board);
                          return (
                            <div
                              key={board.id}
                              className={styles["premium-board-wrapper"]}
                            >
                              <div
                                tabIndex={3}
                                className={
                                  styles["premium-option"] +
                                  " " +
                                  styles[board.color] +
                                  (selectedPremiumBoard === board.id
                                    ? " " + styles["current"]
                                    : "") +
                                  (isOwned ? " " + styles["owned"] : "") +
                                  (thumbnail
                                    ? " " + styles["has-thumbnail"]
                                    : "")
                                }
                                style={
                                  thumbnail
                                    ? {
                                        backgroundImage: `url(${thumbnail})`,
                                        backgroundSize: "contain",
                                        backgroundPosition: "center center",
                                        backgroundRepeat: "no-repeat",
                                      }
                                    : {}
                                }
                                onClick={() => {
                                  game.sounds.clickSound.play();
                                  setPreviewBoard(board);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    game.sounds.clickSound.play();
                                    setPreviewBoard(board);
                                  }
                                }}
                              ></div>
                              <div className={styles["premium-board-name"]}>
                                {board.name}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>

                  {/* Support Section */}
                  <div className={styles["support-section"]} style={{display: "none"}}>
                    <h2 className={styles["support-heading"]}>
                      Support This Project
                    </h2>
                    <div className={styles["creator-mobile"]}>
                      <img
                        src={creatorImage}
                        alt="Creator"
                        className={styles["creator-image-mobile"]}
                      />
                      <a
                        href="https://x.com/runtimeerror55"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={
                          styles["creator-twitter-mobile"] +
                          " " +
                          styles[
                            "creator-twitter-theme-" +
                              theme.replace("theme-", "")
                          ]
                        }
                      >
                        Twitter
                      </a>
                    </div>
                    <div className={styles["support-body"]}>
                      <div className={styles["support-content"]}>
                        <p>
                          This indie game represents 7-8 months of solo
                          development spread over 2 years, multiple iterations
                          from scratch and a genuine passion for creating
                          something special. As an indie game.
                        </p>
                        <p>
                          What started as a simple idea evolved into a complete
                          gaming experience. From optimizing real-time
                          multiplayer functionality to fine-tuning every sound
                          effect , animation and every challenge made this
                          project better. It's been a journey of learning,
                          problem-solving, and pouring countless hours into
                          something I truly believed in.
                        </p>
                        <p>
                          If you're enjoying the game and this work resonates
                          with you, your support would make a big difference. As
                          an indie developer, it helps keep this project alive,
                          enables future updates, and motivates me to create
                          more tools like this.
                        </p>
                        <a
                          href="https://buymeacoffee.com/runtimeerrg"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={
                            styles["support-button"] +
                            " " +
                            styles[
                              "support-button-theme-" +
                                theme.replace("theme-", "")
                            ]
                          }
                          tabIndex={3}
                          style={{display: "none"}}
                        >
                          ☕ Buy Me a Coffee
                        </a>
                        <p className={styles["support-footer"]}>
                          Also check out my other project:{" "}
                          <a
                            href="https://www.minitype.in/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={
                              styles["minitype-link"] +
                              " " +
                              styles[
                                "minitype-link-theme-" +
                                  theme.replace("theme-", "")
                              ]
                            }
                          >
                            MiniType
                          </a>{" "}
                          - a productivity tool for improving your typing skills
                          of any level
                        </p>
                      </div>
                      <div className={styles["creator-desktop"]}>
                        <img
                          src={creatorImage}
                          alt="Creator"
                          className={styles["creator-image"]}
                        />
                        <a
                          href="https://x.com/runtimeerror55"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={
                            styles["creator-twitter"] +
                            " " +
                            styles[
                              "creator-twitter-theme-" +
                                theme.replace("theme-", "")
                            ]
                          }
                        >
                          Twitter
                        </a>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </CardOne>

      {showLoginModal && (
        <div className={styles["modal-overlay"]}>
          <div
            className={
              styles["modal"] +
              " " +
              styles["modal-theme-" + theme.replace("theme-", "")]
            }
          >
            <button
              className={
                styles["close-button"] +
                " " +
                styles["close-button-theme-" + theme.replace("theme-", "")]
              }
              onClick={() => {
                if (game) game.sounds.clickSound.play();
                setShowLoginModal(false);
              }}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2>Login Required</h2>
            <p>Please sign in to purchase premium themes</p>
            <div className={styles["modal-buttons"]}>
              <button
                className={
                  styles["modal-login-btn"] +
                  " " +
                  styles["btn-theme-" + theme.replace("theme-", "")]
                }
                onClick={handleLoginFromModal}
              >
                <FontAwesomeIcon icon={faGoogle} /> SIGN IN
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentModal && selectedTheme && (
        <div
          className={styles["modal-overlay"]}
          onClick={(e) => {
            // Close modal if clicking on overlay (outside the modal box)
            if (e.target === e.currentTarget) {
              if (game) game.sounds.clickSound.play();
              setShowPaymentModal(false);
            }
          }}
        >
          <div
            className={
              styles["modal"] +
              " " +
              styles["modal-theme-" + theme.replace("theme-", "")]
            }
            data-payment-modal="true"
            onKeyDown={(event) => {
              const modal = event.currentTarget;
              const elements = modal.querySelectorAll(
                '[data-modal-button="true"]',
              );
              if (elements.length === 0) return;

              if (!focusableElements.paymentModalIndex) {
                focusableElements.paymentModalIndex = 0;
              }

              if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
                event.preventDefault();
                if (focusableElements.paymentModalIndex <= 0) {
                  focusableElements.paymentModalIndex = elements.length - 1;
                } else {
                  focusableElements.paymentModalIndex--;
                }
                if (elements[focusableElements.paymentModalIndex]) {
                  playSound(game, "navigation");
                  elements[focusableElements.paymentModalIndex].focus();
                }
              } else if (
                event.key === "ArrowDown" ||
                event.key === "ArrowRight"
              ) {
                event.preventDefault();
                if (
                  focusableElements.paymentModalIndex >=
                  elements.length - 1
                ) {
                  focusableElements.paymentModalIndex = 0;
                } else {
                  focusableElements.paymentModalIndex++;
                }
                if (elements[focusableElements.paymentModalIndex]) {
                  playSound(game, "navigation");
                  elements[focusableElements.paymentModalIndex].focus();
                }
              } else if (event.key === "Enter") {
                event.preventDefault();
                if (elements[focusableElements.paymentModalIndex]) {
                  playSound(game, "click");
                  elements[focusableElements.paymentModalIndex].click();
                }
              } else if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                if (game) game.sounds.clickSound.play();
                setShowPaymentModal(false);
              }
            }}
            onMouseMove={(event) => {
              const modal = event.currentTarget;
              const elements = modal.querySelectorAll(
                '[data-modal-button="true"]',
              );
              if (elements.length > 0) {
                // Find which element is hovered
                const hoveredElement = event.target.closest(
                  '[data-modal-button="true"]',
                );
                if (hoveredElement) {
                  const index = Array.from(elements).indexOf(hoveredElement);
                  if (
                    index !== -1 &&
                    index !== focusableElements.paymentModalIndex
                  ) {
                    focusableElements.paymentModalIndex = index;
                    playSound(game, "navigation");
                    hoveredElement.focus();
                  }
                }
              }
            }}
            tabIndex={0}
          >
            <button
              className={
                styles["close-button"] +
                " " +
                styles["close-button-theme-" + theme.replace("theme-", "")]
              }
              onClick={() => {
                if (game) game.sounds.clickSound.play();
                setShowPaymentModal(false);
              }}
              tabIndex={-1}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <h2>Purchase Skin</h2>
            <div className={styles["payment-theme-preview"]}>
              <div
                className={
                  styles["preview-card"] + " " + styles[selectedTheme.color]
                }
              >
                <h3>{selectedTheme.name}</h3>
              </div>
            </div>

            <p>
              Click <strong>Preview</strong> to view images and preview how the
              theme looks before purchasing.
            </p>
            <p>
              Use the same email on Gumroad as your Google account email, then
              login here to see your purchases.
            </p>

            {paymentError && (
              <div className={styles["payment-error"]}>{paymentError}</div>
            )}

            <div className={styles["modal-buttons"]}>
              <button
                className={
                  styles["modal-product-btn"] +
                  " " +
                  styles["btn-theme-" + theme.replace("theme-", "")]
                }
                onClick={handleViewProduct}
                data-modal-button="true"
              >
                PREVIEW
              </button>
              <button
                className={
                  styles["modal-confirm-btn"] +
                  " " +
                  styles["btn-theme-" + theme.replace("theme-", "")]
                }
                onClick={handlePaymentProcess}
                data-modal-button="true"
              >
                BUY
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Board Preview Modal */}
      {previewBoard && (
        <PremiumBoardPreviewModal
          board={previewBoard}
          onClose={() => setPreviewBoard(null)}
          onSelect={async () => {
            const success = await setPremiumBoard(previewBoard.id);
            if (success) setPreviewBoard(null);
          }}
          isOwned={userOwnsBoard(previewBoard)}
          isPurchased={Boolean(
            boardItemsData.find(
              (item) => item.name === previewBoard.boardItemName,
            )?.isOwned,
          )}
          isTrial={Boolean(
            boardItemsData.find(
              (item) => item.name === previewBoard.boardItemName,
            )?.isTrialAccessible,
          )}
          game={game}
          user={user}
        />
      )}

      {/* Skin Preview Modal */}
      {showSkinPreview && selectedTheme && (
        <SkinPreviewModal
          themeData={selectedTheme}
          onClose={() => {
            setShowSkinPreview(false);
            // If opened from payment modal, go back to payment modal
            if (previewOpenedFromPayment) {
              setShowPaymentModal(true);
            }
          }}
          onPurchase={handlePaymentProcess}
          onSelect={async () => {
            const success = await setTheme(selectedTheme.name);
            if (success) {
              game.userSavedData.settings.skin = selectedTheme.cssClass;
              game.saveData();
              setShowSkinPreview(false);
            }
          }}
          game={game}
          isOwned={userOwnsTheme(selectedTheme)}
          isTrial={Boolean(selectedTheme?.isTrialAccessible)}
        />
      )}
    </>
  );
};
